import { useMemo, useState } from "react";
import ModalShell from "../../../../_components/modal/ModalShell";
import ConfirmDialog from "../../../../_components/modal/ConfirmDialog";
import DynamicForm from "../../../../_components/form/DynamicForm";
import SegmentedToggle from "../../../../_components/ui/SegmentedToggle";
import TextInput from "../../../../_components/ui/TextInput";
import { useBillingUnitFormSchema } from "./hooks/useBillingUnitFormSchema";
import { useBillingUnitFormState } from "./hooks/useBillingUnitFormState";
import "./_billing-unit-form-modal.scss";

const BUID_MODE_OPTIONS = [
  { label: "Generate automatically", value: "generate" },
  { label: "Type manually", value: "manual" },
];

/**
 * UI Layer - presentational composition only, mirrors
 * _features/markets/modals/MarketFormModal/index.jsx exactly. Every
 * business decision (unsaved-changes tracking, submit orchestration) lives
 * in ./hooks/*; this file just wires their outputs into JSX. Billing Units
 * has no schema post-processing rule (see ./utils/billingUnitFormRules.js),
 * so the raw schema from useBillingUnitFormSchema is used directly - no
 * processedFormSchema step like Markets needed for its country-field swap.
 */
const BillingUnitFormModal = ({ isOpen, onClose, rowData, mode, onSuccess, refetchFilters }) => {
  const formSchema = useBillingUnitFormSchema();

  // BUID isn't part of the object-customization schema (it's a system-
  // assigned key other domains - Wallet, Usage Metering - key off of
  // instead of the ObjectId, per docs/domains/08-billing-units.md), so it
  // isn't a DynamicForm field. Create-only: "Generate automatically" (the
  // existing behavior) or "Type manually" for a caller-chosen id; once
  // created, BUID is immutable (edit mode below just displays it).
  const [buidMode, setBuidMode] = useState("generate");
  const [customBuidValue, setCustomBuidValue] = useState("");
  const trimmedCustomBuid = customBuidValue.trim();
  const isCustomBuidMissing = mode === "create" && buidMode === "manual" && !trimmedCustomBuid;

  const {
    control,
    formData,
    errors,
    handleChange,
    handleBlur,
    showCloseConfirmation,
    isSubmitting,
    attemptClose,
    confirmDiscard,
    cancelDiscard,
    onSubmit,
  } = useBillingUnitFormState(formSchema, {
    mode,
    rowData,
    isOpen,
    onClose,
    onSuccess,
    refetchFilters,
    customBuid: buidMode === "manual" ? trimmedCustomBuid : undefined,
  });

  const title = useMemo(() => (mode === "edit" ? "Edit billing unit" : "New billing unit"), [mode]);
  const description = useMemo(
    () =>
      mode === "edit"
        ? "Updates this consumption tracker's name and status."
        : "Adds a new tracker for a unit of customer consumption.",
    [mode]
  );

  return (
    <>
      <ModalShell
        isOpen={isOpen}
        onRequestClose={attemptClose}
        width="460px"
        height="100vh"
        center={false}
        right={0}
        className="billing-unit-form-modal--right-docked"
        title={title}
        description={description}
        isSubmitting={isSubmitting}
        footerButtons={[
          { children: "Close", onClick: attemptClose, variant: "outlined" },
          {
            children: mode === "edit" ? "Update" : "Create",
            onClick: onSubmit,
            variant: "contained",
            loading: isSubmitting,
            disabled: isSubmitting || isCustomBuidMissing,
          },
        ]}
      >
        <form onSubmit={onSubmit}>
          <DynamicForm
            schema={formSchema}
            control={control}
            formData={formData}
            errors={errors}
            handleChange={handleChange}
            handleBlur={handleBlur}
            mode={mode}
            disabled={isSubmitting}
          />
          <div className="billing-unit-form-modal__buid-section">
            {mode === "edit" ? (
              <TextInput id="buid" label="BUID" value={rowData?.BUID} disabled locked badge="SYSTEM" />
            ) : (
              <>
                <SegmentedToggle
                  id="buid-mode"
                  label="BUID"
                  options={BUID_MODE_OPTIONS}
                  value={buidMode}
                  onChange={setBuidMode}
                  disabled={isSubmitting}
                />
                {buidMode === "manual" && (
                  <TextInput
                    id="buid"
                    value={customBuidValue}
                    onChange={setCustomBuidValue}
                    placeholder="e.g. BUID20260904001"
                    disabled={isSubmitting}
                    required
                  />
                )}
              </>
            )}
          </div>
        </form>
      </ModalShell>

      <ConfirmDialog
        isOpen={showCloseConfirmation}
        title="Discard unsaved changes?"
        description="You have unsaved changes. Are you sure you want to close without saving?"
        cancelText="Keep editing"
        confirmText="Discard changes"
        onCancel={cancelDiscard}
        onConfirm={confirmDiscard}
      />
    </>
  );
};

export default BillingUnitFormModal;
